import { ChevronRight } from 'lucide-react';
import type { KeyboardEvent } from 'react';
import { ENUM_LABELS, FLAG_LABELS } from '#shared/types';
import { Badge } from '#client/components/ui/Badge';
import { cn } from '#client/lib/cn';
import { dash } from '#client/lib/format';
import type { ResultRowView } from './result-entry.logic';

interface ResultRowProps {
  index: number;
  result: string;
  /** What the row shows, from `describeResultRow`. */
  row: ResultRowView;
  readOnly: boolean;
  isOpen: boolean;
  onToggle: () => void;
  onResultChange: (value: string) => void;
  onRangeChange: (valueId: number) => void;
  onKeyDown: (event: KeyboardEvent) => void;
  registerRef: (element: HTMLInputElement | HTMLSelectElement | null) => void;
}

/** Every field the row and its parts read, flattened for the sub-components' picks. */
type RowFields = ResultRowView & Omit<ResultRowProps, 'row'>;

export function ResultRow({
  index,
  result,
  row,
  readOnly,
  isOpen,
  onToggle,
  onResultChange,
  onRangeChange,
  onKeyDown,
  registerRef,
}: ResultRowProps) {
  const { name, code, unit, range, recordedRange, recordedUnit, presets, ranges } = row;
  const { selectedRangeId, flag, formula } = row;
  const rowId = `result-row-${index}`;
  const panelId = `result-panel-${index}`;

  return (
    <>
      <tr className={cn('c-table__row', flag && `c-entry__row--${flag}`)}>
        <td className="c-table__td c-entry__chevron-col">
          {/* Only offered when there is something to expand to. */}
          {ranges.length > 0 && (
            <ExpandToggle
              name={name}
              rowId={rowId}
              panelId={panelId}
              isOpen={isOpen}
              onToggle={onToggle}
            />
          )}
        </td>

        <td className="c-table__td">
          <strong>{name}</strong>
        </td>

        <td className="c-table__td c-table__td--control c-entry__result-col">
          <ResultInput
            name={name}
            result={result}
            formula={formula}
            presets={presets}
            readOnly={readOnly}
            onResultChange={onResultChange}
            onKeyDown={onKeyDown}
            registerRef={registerRef}
          />
        </td>

        <td className="c-table__td c-entry__unit">{dash(unit)}</td>
        <td className="c-table__td c-entry__range tnum">{dash(range)}</td>

        <td className="c-table__td c-table__cell--end">
          {flag && <Badge tone={flag === 'high' ? 'severe' : 'info'}>{FLAG_LABELS[flag]}</Badge>}
        </td>
      </tr>

      {isOpen && (
        <RangePanel
          index={index}
          rowId={rowId}
          panelId={panelId}
          ranges={ranges}
          selectedRangeId={selectedRangeId}
          recordedRange={recordedRange}
          recordedUnit={recordedUnit}
          code={code}
          readOnly={readOnly}
          onRangeChange={onRangeChange}
        />
      )}
    </>
  );
}

type ResultInputProps = Pick<
  RowFields,
  | 'name'
  | 'result'
  | 'formula'
  | 'presets'
  | 'readOnly'
  | 'onResultChange'
  | 'onKeyDown'
  | 'registerRef'
>;

/** The result field: a calculated value, a select of preset answers, or free text. */
function ResultInput({
  name,
  result,
  formula,
  presets,
  readOnly,
  onResultChange,
  onKeyDown,
  registerRef,
}: ResultInputProps) {
  // No field and no ref: keyboard movement steps over a calculated row.
  if (formula !== null) {
    return (
      <span className="c-entry__calculated" title={`Calculated: ${formula}`}>
        <output aria-label={`${name}, calculated as ${formula}`} className="tnum">
          {dash(result)}
        </output>
        <Badge tone="neutral">ƒ calc</Badge>
      </span>
    );
  }

  if (presets.length > 0) {
    return (
      <select
        ref={registerRef}
        className="c-select__control c-entry__input"
        aria-label={`Result for ${name}`}
        value={result}
        disabled={readOnly}
        onChange={(event) => onResultChange(event.target.value)}
        onKeyDown={onKeyDown}
      >
        <option value="">—</option>
        {presets.map((preset) => (
          <option key={preset} value={preset}>
            {preset}
          </option>
        ))}
      </select>
    );
  }

  return (
    <input
      ref={registerRef}
      className="c-entry__input"
      aria-label={`Result for ${name}`}
      value={result}
      disabled={readOnly}
      // `text` with inputMode rather than `type="number"`: a number
      // input loses its value to the scroll wheel and rejects the
      // "<0.5" a technician legitimately types below a detection limit.
      inputMode="decimal"
      autoComplete="off"
      enterKeyHint="next"
      placeholder="—"
      onChange={(event) => onResultChange(event.target.value)}
      onKeyDown={onKeyDown}
    />
  );
}

type RangePanelProps = Pick<
  RowFields,
  | 'index'
  | 'ranges'
  | 'selectedRangeId'
  | 'recordedRange'
  | 'recordedUnit'
  | 'code'
  | 'readOnly'
  | 'onRangeChange'
> & { rowId: string; panelId: string };

/** The expansion: which cohort range applies, the recorded range and the parameter code. */
function RangePanel({
  index,
  rowId,
  panelId,
  ranges,
  selectedRangeId,
  recordedRange,
  recordedUnit,
  code,
  readOnly,
  onRangeChange,
}: RangePanelProps) {
  return (
    <tr className="c-entry__panel-row">
      <td className="c-table__td" colSpan={6} id={panelId} aria-labelledby={rowId}>
        <div className="c-entry__panel">
          <fieldset className="c-entry__ranges">
            <legend className="c-detail-list__label">Which reference range applies</legend>

            {/*
              A radio group, not a dropdown: there are two to five cohort
              ranges and the point is to compare them, which a collapsed
              select prevents. Choosing one is what the server snapshots
              onto the report, so it must be deliberate.
            */}
            {ranges.map((option) => (
              <label key={option.id} className="c-entry__range-option">
                <input
                  type="radio"
                  name={`range-${index}`}
                  checked={option.id === selectedRangeId}
                  disabled={readOnly}
                  onChange={() => onRangeChange(option.id)}
                />
                <span>
                  <span className="tnum">
                    {option.normal_range} {option.unit}
                  </span>
                  {(option.gender || option.age_group) && (
                    <span className="c-entry__cohort">
                      {[option.gender ? ENUM_LABELS.gender[option.gender] : null, option.age_group]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  )}
                </span>
              </label>
            ))}
          </fieldset>

          <dl className="c-detail-list c-entry__meta">
            <div className="c-detail-list__item">
              <dt className="c-detail-list__label">Recorded range</dt>
              <dd className="c-detail-list__value tnum">
                {dash(recordedRange)} {recordedUnit}
              </dd>
            </div>
            {code && (
              <div className="c-detail-list__item">
                <dt className="c-detail-list__label">Parameter code</dt>
                <dd className="c-detail-list__value c-detail-list__value--mono">{code}</dd>
              </div>
            )}
          </dl>
        </div>
      </td>
    </tr>
  );
}

/** The chevron that opens a row's range panel. */
function ExpandToggle({
  name,
  rowId,
  panelId,
  isOpen,
  onToggle,
}: {
  name: string;
  rowId: string;
  panelId: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={cn('c-entry__chevron', isOpen && 'is-open')}
      aria-expanded={isOpen}
      aria-controls={panelId}
      id={rowId}
      onClick={onToggle}
      // Alt is required: a bare letter key would capture typing.
      onKeyDown={(event) => {
        if (event.altKey && event.key.toLowerCase() === 'e') {
          event.preventDefault();
          onToggle();
        }
      }}
    >
      <ChevronRight aria-hidden="true" />
      <span className="sr-only">
        {isOpen ? 'Hide' : 'Show'} reference ranges for {name}
      </span>
    </button>
  );
}
